import Compressor from 'compressorjs';
import { format } from 'date-fns';
import gifsicle from 'gifsicle-wasm-browser';
import { useMemo } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';

import { useSnackbars } from '@customerio/pluma-components/react';

import { useStore } from '@nanostores/react';
import { atom } from 'nanostores';

import { type Asset } from '@/services/db/asset/entity';
import { sentryCaptureException } from '@/utils/sentry';
import { isEmptyString } from '@/utils/string';

import { $assetUploadState } from '../upload/asset-upload-modal';
import { fileSize, getImageDimensions } from '../utils';
import { useAssetUpload } from '../upload';

export const MAX_IMAGE_WIDTH = 1600;
export type CompressionType = 'regular' | 'good' | 'maximum';
export type CompressionMode = 'normal' | 'retina';

type OptimizerModalState = {
  originalAsset: Asset;
  onCleanup?: () => void;
} | null;

const $optimizerModal = atom<OptimizerModalState>(null);

export function useAssetOptimizerModal() {
  const state = useStore($optimizerModal);

  return {
    isOpen: state !== null,
    state,
    show: (data: NonNullable<OptimizerModalState>) => $optimizerModal.set(data),
    close: () => {
      state?.onCleanup?.();
      $optimizerModal.set(null);
    },
  };
}

async function measureBlobDimensions(blob: Blob) {
  const temporaryUrl = URL.createObjectURL(blob);
  try {
    return await getImageDimensions(temporaryUrl);
  } finally {
    URL.revokeObjectURL(temporaryUrl);
  }
}

export function useSaveOptimizedAsset() {
  const { close, state } = useAssetOptimizerModal();
  const upload = useAssetUpload();
  const snackbars = useSnackbars();

  const { mutateAsync: saveOptimizedAsset, isPending: isUploading } = useMutation({
    mutationFn: async (optimizedAssetBlob: Blob) => {
      const { originalAsset } = state ?? {};
      if (originalAsset == null) return;

      const lastDot = originalAsset.name.lastIndexOf('.');
      const baseFileName = lastDot > 0 ? originalAsset.name.slice(0, lastDot) : originalAsset.name;
      const fileExtension = lastDot > 0 ? originalAsset.name.slice(lastDot) : '';
      const formattedDateTime = format(new Date(), 'PPpp').replaceAll(':', '-');
      const optimizedFileName = `${baseFileName} optimized ${formattedDateTime}${fileExtension}`;

      const file = new File([optimizedAssetBlob], optimizedFileName, {
        type: optimizedAssetBlob.type,
      });

      // Set the parent folder so the optimized file uploads to the same folder
      // as the original asset (not root).
      $assetUploadState.set({
        ...$assetUploadState.get(),
        parentFolderId: originalAsset.parent_id,
      });

      await upload({
        files: [file],
        allowedFileCategories: ['image'],
      });

      close();
    },
    onError: (error) => {
      sentryCaptureException(error);
      snackbars.create({
        variant: 'error',
        message: 'Error uploading optimized asset.',
      });
    },
    networkMode: 'always',
    retry: false,
  });

  return { saveOptimizedAsset, isUploading };
}

export function useOriginalAsset({ originalAsset }: { originalAsset: Asset | undefined }) {
  const { data } = useQuery({
    queryKey: ['asset-details', originalAsset?.path],
    queryFn: async () => {
      if (originalAsset == null) throw new Error('No asset');
      const response = await fetch(originalAsset.path);
      const blob = await response.blob();
      const dims = await measureBlobDimensions(blob);
      const size = fileSize(originalAsset.size);

      return {
        blob,
        details: {
          width: dims?.width ?? 0,
          height: dims?.height ?? 0,
          size,
          type: blob.type,
        },
      };
    },
    enabled: !isEmptyString(originalAsset?.path),
    networkMode: 'always',
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  return { originalAsset, ...data };
}

export function useOptimizeAsset({
  originalAssetPath,
  originalAssetBlob,
  compressionType,
  compressionMode,
}: {
  originalAssetPath: string | undefined;
  originalAssetBlob: Blob | null;
  compressionType: CompressionType;
  compressionMode: CompressionMode;
}) {
  const { optimizeGif } = useGifOptimizer();
  const { optimizeImage } = useImageOptimizer();

  const {
    data: state,
    isLoading: isOptimizing,
    isError,
    error,
  } = useQuery({
    queryKey: ['optimized-asset', originalAssetPath, compressionMode, compressionType],
    queryFn: async () => {
      if (originalAssetBlob == null) throw new Error('No blob');

      const optimize = originalAssetBlob.type === 'image/gif' ? optimizeGif : optimizeImage;

      const optimizedAssetBlob = await optimize({
        inputBlob: originalAssetBlob,
        options: { compressionMode, compressionType },
      });

      const dims = await measureBlobDimensions(optimizedAssetBlob);

      const sizeDeltaPercent = Math.round(
        ((originalAssetBlob.size - optimizedAssetBlob.size) / originalAssetBlob.size) * 100,
      );

      return {
        blob: optimizedAssetBlob,
        details: {
          size: fileSize(optimizedAssetBlob.size),
          sizeDelta: `${sizeDeltaPercent}%`,
          sizeDeltaPercent,
          width: dims?.width,
          height: dims?.height,
          type: optimizedAssetBlob.type,
        },
      };
    },
    enabled: originalAssetBlob != null,
    networkMode: 'always',
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  return { ...state, isOptimizing, isError, error };
}

export function useImageOptimizer() {
  const qualityOptions = useMemo<{
    [key in CompressionMode]: { [key in CompressionType]: Compressor.Options };
  }>(() => {
    return {
      normal: {
        regular: { quality: 0.8 },
        good: { quality: 0.75, convertSize: 2_000_000, maxWidth: MAX_IMAGE_WIDTH },
        maximum: { quality: 0.6, convertSize: 1_500_000, maxWidth: MAX_IMAGE_WIDTH },
      },
      retina: {
        regular: { quality: 0.5 },
        good: { quality: 0.35, convertSize: 2_000_000, maxWidth: MAX_IMAGE_WIDTH * 2 },
        maximum: { quality: 0.25, convertSize: 1_500_000, maxWidth: MAX_IMAGE_WIDTH * 2 },
      },
    };
  }, []);

  const { mutateAsync: optimizeImage } = useMutation({
    mutationFn: async ({
      inputBlob,
      options,
    }: {
      inputBlob: Blob;
      options: { compressionMode: CompressionMode; compressionType: CompressionType };
    }) => {
      return new Promise<Blob>((resolve, reject) => {
        new Compressor(inputBlob, {
          retainExif: true,
          ...qualityOptions[options.compressionMode][options.compressionType],
          success: resolve,
          error: reject,
        });
      });
    },
    networkMode: 'always',
    retry: false,
  });

  return { optimizeImage };
}

export function useGifOptimizer() {
  const gifsicleOptions = useMemo(
    () => ({
      normal: {
        regular: ['--optimize=2'],
        good: ['--optimize=2', '--colors=128'],
        maximum: ['--optimize=3', '--colors=64', '--lossy=80'],
      },
      retina: {
        regular: ['--optimize=1', '--scale=2'],
        good: ['--optimize=2', '--colors=128', '--scale=2'],
        maximum: ['--optimize=3', '--colors=64', '--lossy=80', '--scale=2'],
      },
    }),
    [],
  );

  const { mutateAsync: optimizeGif } = useMutation({
    mutationFn: async ({
      inputBlob,
      options,
    }: {
      inputBlob: Blob;
      options: { compressionMode: CompressionMode; compressionType: CompressionType };
    }) => {
      const optimizationCommands = gifsicleOptions[options.compressionMode][options.compressionType].join(' ');

      const fullCommand = [optimizationCommands, 'input.gif -o /out/out.gif'].filter(Boolean).join(' ');

      const result = await gifsicle.run({
        input: [{ file: inputBlob, name: 'input.gif' }],
        command: [fullCommand],
      });

      const optimizedGif = result[0];
      if (optimizedGif == null) {
        throw new Error('GIF optimization produced no output.');
      }
      const optimizedBlob = new Blob([optimizedGif as Uint8Array<ArrayBuffer>], { type: 'image/gif' });

      return optimizedBlob;
    },
    networkMode: 'always',
    retry: false,
  });

  return { optimizeGif };
}
